// Dr.Rx™ Admin Ops Command Center
import React, { useState } from 'react';
import type { View } from '../types';
import LiveSystemMonitor from './admin/LiveSystemMonitor';
import AlertsStudioWidget from './admin/AlertsStudioWidget';
import EditorialStudio from './admin/EditorialStudio';
import SystemControls from './admin/SystemControls';
import SecurityStudioWidget from './admin/SecurityStudioWidget';
import ErrorLogsWidget from './admin/ErrorLogsWidget';
import SEO from './SEO';

interface AdminDashboardProps {
    onNavigate: (view: View) => void;
}

type AdminTab = 'Monitor' | 'Alerts' | 'Editorial' | 'Controls' | 'Security' | 'Logs';

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onNavigate }) => {
    const [activeTab, setActiveTab] = useState<AdminTab>('Monitor');

    const tabs: { id: AdminTab; label: string; subtitle: string }[] = [
        { id: 'Monitor', label: '📊 Live Monitor', subtitle: 'Telemetry & Latency' },
        { id: 'Alerts', label: '🚨 Alerts Studio', subtitle: 'FDA / CDC / DEA Feed' },
        { id: 'Editorial', label: '✍️ Editorial Studio', subtitle: 'Clinical Pearls CMS' },
        { id: 'Security', label: '🔒 Security Studio', subtitle: 'Rules & PHI Audit' },
        { id: 'Controls', label: '⚙️ System Controls', subtitle: 'Maintenance & Ingestion' },
        { id: 'Logs', label: '📜 System Logs', subtitle: 'Exception Stream' },
    ];

    return (
        <div className="min-h-screen bg-[#0B1325] text-white p-4 sm:p-6 lg:p-8 font-sans">
            <SEO 
                title="Ops Command Center - Dr.Rx™" 
                description="Administrative command center for Dr.Rx."
                noindex={true}
            />
            
            <header className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-6 bg-[#15243E]/80 p-5 rounded-2xl backdrop-blur-xl sticky top-4 z-40 border border-yellow-400/25 shadow-2xl gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-amber-500 text-slate-950 rounded-xl flex items-center justify-center text-xl font-black shadow-lg shadow-yellow-400/20">
                        Rx
                    </div>
                    <div>
                        <div className="flex items-center gap-2.5">
                            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Dr.Rx™ Ops Command Center</h1>
                            <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                Verified Admin
                            </span>
                        </div>
                        <p className="text-gray-400 text-xs mt-0.5">NV Pharmaceutics LLC • Production Clinical Infrastructure &amp; Security Telemetry</p>
                    </div>
                </div>
                <button
                    onClick={() => onNavigate('dashboard')}
                    className="px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-yellow-400/40 rounded-xl text-xs font-bold uppercase tracking-wider text-gray-200 hover:text-yellow-300 transition-all flex items-center gap-2"
                >
                    <span>← Return to Clinical App</span>
                </button>
            </header>

            <nav className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-8">
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`p-3 rounded-xl text-left transition-all border ${
                                isActive 
                                ? 'bg-gradient-to-br from-[#1E335C] to-[#15243E] border-yellow-400/60 text-white shadow-lg shadow-yellow-400/10' 
                                : 'bg-[#15243E]/50 border-blue-900/40 text-gray-400 hover:bg-[#1E2E4A]/70 hover:text-gray-200'
                            }`}
                        >
                            <div className={`text-xs sm:text-sm font-bold truncate ${isActive ? 'text-yellow-400' : 'text-white'}`}>
                                {tab.label}
                            </div>
                            <div className="text-[10px] text-gray-400 truncate mt-0.5">
                                {tab.subtitle}
                            </div>
                        </button>
                    );
                })}
            </nav>

            <main className="max-w-7xl mx-auto pb-16">
                {activeTab === 'Monitor' && <div className="animate-fade-in"><LiveSystemMonitor /></div>}
                {activeTab === 'Alerts' && <div className="animate-fade-in"><AlertsStudioWidget /></div>}
                {activeTab === 'Editorial' && <div className="animate-fade-in"><EditorialStudio /></div>}
                {activeTab === 'Security' && <div className="animate-fade-in"><SecurityStudioWidget /></div>}
                {activeTab === 'Controls' && <div className="animate-fade-in"><SystemControls /></div>}
                {activeTab === 'Logs' && <div className="animate-fade-in"><ErrorLogsWidget /></div>}
            </main>
        </div>
    );
};

export default AdminDashboard;
